import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { Link } from "wouter";
import { toast } from "sonner";
import { Play, TrendingUp, ArrowRight, Loader2 } from "lucide-react";

export function TemplateWidget() {
  const { selectedCompanyId } = useCompany();

  // Fetch popular templates
  const { data: templates = [], isLoading } =
    trpc.transactionTemplates.getPopular.useQuery(
      { companyId: selectedCompanyId || 1, limit: 5 },
      { enabled: !!selectedCompanyId }
    );

  // Use template mutation
  const useTemplateMutation = trpc.transactionTemplates.useTemplate.useMutation(
    {
      onSuccess: () => {
        toast.success("Transaction created from template");
      },
      onError: error => {
        toast.error(`Failed to create transaction: ${error.message}`);
      },
    }
  );

  const handleQuickUse = (template: any) => {
    if (confirm(`Create transaction from "${template.name}"?`)) {
      useTemplateMutation.mutate({
        templateId: template.id,
        companyId: selectedCompanyId || 1,
        date: Date.now(),
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Templates</CardTitle>
          <CardDescription>Popular transaction templates</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (templates.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Templates</CardTitle>
          <CardDescription>Popular transaction templates</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground mb-4">
              No templates yet. Create your first template to get started.
            </p>
            <Link href="/templates">
              <Button size="sm" variant="outline">
                Create Template
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Quick Templates</CardTitle>
            <CardDescription>Most used templates</CardDescription>
          </div>
          <Link href="/templates">
            <Button size="sm" variant="ghost">
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {templates.map((template: any) => (
            <div
              key={template.id}
              className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-medium truncate">
                    {template.name}
                  </h4>
                  {template.usageCount > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      {template.usageCount}
                    </Badge>
                  )}
                </div>
                {template.defaultAmount && (
                  <p className="text-xs text-muted-foreground">
                    CHF {(template.defaultAmount / 100).toFixed(2)}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleQuickUse(template)}
                disabled={useTemplateMutation.isPending}
                className="ml-2 shrink-0"
              >
                <Play className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <Link href="/templates">
          <Button variant="outline" size="sm" className="w-full mt-4">
            View All Templates
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
